import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useIsMobileLayout } from '@/hooks';
import { shortFormatDate } from '@/utils/dates';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

export type Props = {
    currency: string;
    date: number;
    invoiceId: number;
    sellerName: string;
    title: string;
    totalPrice: number;
};

const WonItemsCardHeaderComponent = ({ currency, date, invoiceId, sellerName, title, totalPrice }: Props) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();
    const catalogDate = shortFormatDate(date);

    const mobileTextStyles = 'mb-2xs block max-w-full overflow-hidden text-ellipsis whitespace-nowrap';
    const desktopTextStyles = 'mb-2xs mr-lg inline-block';
    const textStyles = isMobile ? mobileTextStyles : desktopTextStyles;

    const invoiceTotal = formatMessage(['wonitems', 'catalog', 'total_label'], {
        values: {
            amount: (
                <FormattedCurrency
                    key="invoice-total"
                    sourceCurrency={currency}
                    value={totalPrice}
                />
            ),
        },
    });

    return (
        <section
            className={cn(
                'flex min-h-[40px] flex-col items-start bg-background-subdued py-sm text-text-secondary transition-all duration-[250ms]',
                isMobile ? 'px-md' : 'flex-row justify-between px-lg'
            )}
        >
            <div className={cn(isMobile ? 'block' : 'flex')}>
                {Boolean(invoiceId) && (
                    <BodySecondary
                        className={textStyles}
                        color="secondary"
                    >
                        {formatMessage(['wonitems', 'badges', 'invoice'], {
                            values: { invoiceId },
                        })}
                    </BodySecondary>
                )}
                <BodySecondary
                    className={textStyles}
                    color="secondary"
                >
                    {catalogDate}
                </BodySecondary>
                <BodySecondary
                    className={textStyles}
                    color="secondary"
                >
                    {title}
                </BodySecondary>
                <BodySecondary
                    className={textStyles}
                    color="secondary"
                >
                    {sellerName}
                </BodySecondary>
            </div>
            {Boolean(invoiceId) && (
                <div className="text-right whitespace-nowrap">
                    {!isMobile ? (
                        <BodyPrimary data-testid="totalValue">{invoiceTotal}</BodyPrimary>
                    ) : (
                        <BodySecondary className={mobileTextStyles}>{invoiceTotal}</BodySecondary>
                    )}
                </div>
            )}
        </section>
    );
};

export const WonItemsCardHeader = memo<Props>(WonItemsCardHeaderComponent);
